<template>
  <div class="owner-repair-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('ownerRepairManage.phoneRepair')" :subtitle="$t('ownerRepairManage.page.subtitle')">
      <template #extra>
        <el-button v-if="ownerRepairManageInfo.conditions.roomId != ''" type="primary" icon="el-icon-plus"
          @click="_openAddOwnerRepairModal">
          {{ $t('ownerRepairManage.register') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="ownerRepairManageInfo.conditions.repairId" clearable class="w-full"
            :placeholder="$t('ownerRepairManage.placeholder.repairId')" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="ownerRepairManageInfo.conditions.repairName" clearable class="w-full"
            :placeholder="$t('ownerRepairManage.placeholder.repairName')" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="ownerRepairManageInfo.conditions.tel" clearable class="w-full"
            :placeholder="$t('ownerRepairManage.placeholder.tel')" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="ownerRepairManageInfo.conditions.repairType" clearable class="w-full"
            :placeholder="$t('ownerRepairManage.placeholder.repairType')">
            <el-option v-for="(item, index) in ownerRepairManageInfo.repairTypes" :key="index"
              :label="item.repairTypeName" :value="item.repairType" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="ownerRepairManageInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('ownerRepairManage.placeholder.state')">
            <el-option v-for="(item, index) in ownerRepairManageInfo.states" :key="index" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryOwnerRepairMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetOwnerRepairMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="ownerRepairManageInfo.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_queryOwnerRepairMethod">
        <template #left>
          <span class="ds-panel__title">{{ $t('ownerRepairManage.phoneRepair') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="ownerRepairManageInfo.ownerRepairs" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('ownerRepairManage.page.empty')"
        @retry="_queryOwnerRepairMethod">
        <el-table-column prop="repairId" :label="$t('ownerRepairManage.table.repairId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="repairObjName" :label="$t('ownerRepairManage.table.location')" align="center" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('ownerRepairManage.table.repairType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairName" :label="$t('ownerRepairManage.table.repairName')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.repairName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="tel" :label="$t('ownerRepairManage.table.tel')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('ownerRepairManage.table.appointmentTime')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="timeout" :label="$t('ownerRepairManage.table.timeout')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.timeout || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('ownerRepairManage.table.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('ownerRepairManage.table.state')" align="center" width="140">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
              :type="getStateType(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="160">
          <template slot-scope="scope">
            <el-button
              v-if="hasPrivilege('502021012067300023') && (scope.row.state == '1000' || scope.row.state == '1200')"
              size="mini" type="primary" @click="_openEditOwnerRepairModel(scope.row)">{{ $t('common.edit')
              }}</el-button>
            <el-button
              v-if="hasPrivilege('502021012051410024') && (scope.row.state == '1000' || scope.row.state == '1200')"
              size="mini" type="danger" @click="_openDeleteOwnerRepairModel(scope.row)">{{ $t('common.delete')
              }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="ownerRepairManageInfo.total" :page="ownerRepairManageInfo.page" :size="ownerRepairManageInfo.rows"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="ds-text-muted">{{ $t('ownerRepairManage.tip1') }}</div>
          <div class="ds-text-muted">{{ $t('ownerRepairManage.tip2') }}</div>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 添加报修模态框 -->
    <add-owner-repair ref="addOwnerRepair" @success="_queryOwnerRepairMethod" />
    <!-- 修改报修模态框 -->
    <edit-owner-repair ref="editOwnerRepair" @success="_queryOwnerRepairMethod" />
    <!-- 删除报修模态框 -->
    <delete-owner-repair ref="deleteOwnerRepair" @success="_queryOwnerRepairMethod" />
  </div>
</template>

<script>
import * as ownerRepairManageApi from '@/api/work/ownerRepairManageApi'
import AddOwnerRepair from '@/components/work/addOwnerRepair'
import EditOwnerRepair from '@/components/work/editOwnerRepair'
import DeleteOwnerRepair from '@/components/work/deleteOwnerRepair'
import { getDict } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'OwnerRepairManageList',
  components: {
    AddOwnerRepair,
    EditOwnerRepair,
    DeleteOwnerRepair,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      ownerRepairManageInfo: {
        ownerRepairs: [],
        total: 0,
        page: 1,
        rows: 10,
        moreCondition: false,
        repairTypes: [],
        states: [],
        conditions: {
          repairId: '',
          repairName: '',
          tel: '',
          repairType: '',
          state: '',
          reqSource: 'pc_mobile'
        }
      }
    }
  },
  created() {
    this._listOwnerRepairs(1, this.ownerRepairManageInfo.rows)
    this._listRepairTypes(1, 50)
    this._loadStates()
  },
  methods: {
    _loadStates() {
      getDict('r_repair_pool', 'state').then(data => {
        this.ownerRepairManageInfo.states = data
      })
    },
    async _listRepairTypes(page, rows) {
      const param = {
        page: page,
        row: rows,
        communityId: this.getCommunityId()
      }
      const { data } = await ownerRepairManageApi.listRepairSettings(param)
      this.ownerRepairManageInfo.repairTypes = data

    },
    _listOwnerRepairs(page, rows) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      this.ownerRepairManageInfo.conditions.page = page
      this.ownerRepairManageInfo.conditions.row = rows
      this.ownerRepairManageInfo.conditions.communityId = this.getCommunityId()
      ownerRepairManageApi.listOwnerRepairs(this.ownerRepairManageInfo.conditions).then(res => {
        if (res.code === 0) {
          this.ownerRepairManageInfo.ownerRepairs = res.data
          this.ownerRepairManageInfo.total = res.total
          this.ownerRepairManageInfo.page = page
          this.ownerRepairManageInfo.rows = rows
        }
      }).catch(() => {
        this.hasError = true
        this.errorMessage = this.$t('ownerRepairManage.page.fetchError')
      }).finally(() => {
        this.loading = false
      })
    },
    _resetListOwnerRepairs() {
      this.ownerRepairManageInfo.conditions = {
        repairId: '',
        repairName: '',
        tel: '',
        repairType: '',
        state: '',
        reqSource: 'pc_mobile'
      }
      this._listOwnerRepairs(1, this.ownerRepairManageInfo.rows)
    },
    _openAddOwnerRepairModal() {
      this.$refs.addOwnerRepair.open()
    },
    _openEditOwnerRepairModel(row) {
      this.$refs.editOwnerRepair.open(row)
    },
    _openDeleteOwnerRepairModel(row) {
      this.$refs.deleteOwnerRepair.open(row)
    },
    _queryOwnerRepairMethod() {
      this._listOwnerRepairs(1, this.ownerRepairManageInfo.rows)
    },
    _resetOwnerRepairMethod() {
      this._resetListOwnerRepairs()
    },
    getStateType(state) {
      switch (state) {
        case '1000': return 'info'
        case '1800': return 'warning'
        case '1900': return 'success'
        case '2001': return 'muted'
        default: return 'info'
      }
    },
    handleSizeChange(val) {
      this.ownerRepairManageInfo.rows = val
      this._listOwnerRepairs(1, val)
    },
    handleCurrentChange(val) {
      this.ownerRepairManageInfo.page = val
      this._listOwnerRepairs(val, this.ownerRepairManageInfo.rows)
    }
  }
}
</script>

<style lang="scss" scoped>
.owner-repair-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
